<script>
export default {
  computed: {
    LayoutComponent() {
      return (
        (this.$route.meta && this.$route.meta.layout) ||
        require('@layouts/main').default
      )
    },
  },
}
</script>

<template>
  <div id="app">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView
      :key="
        $route.matched.length === 1
          ? $route.fullPath
          : $route.matched[0] && $route.matched[0].path
      "
    />
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
@import '~bootstrap/dist/css/bootstrap.css';
@import '~bootstrap-vue-next/dist/bootstrap-vue-next.css';
@import '~floating-vue/dist/style.css';
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
// @import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Design variables and utilities from src/design.
@import '@design';

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body,
#app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  font-size: 15px;
}

body {
  background: $color-body-bg;
}

#app {
  @extend %typography-small;
}

// ===
// Base element styles
// ===

a,
a:visited {
  color: $color-link-text;
  text-decoration: none;

  &:hover {
    color: $color-link-text-active;
    text-decoration: underline;
  }

  &[href^='tel:'] {
    color: inherit;
  }
}

h1 {
  @extend %typography-xxlarge;

  color: lighten($color-text, 17%);
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

@media only screen and (min-width: $size-breakpoint-sm + 1px) {
  h1 {
    margin: 0 0 $size-grid-padding * 2;
  }

  h2 {
    margin: $size-grid-padding * 3 0 $size-grid-padding * 2;
  }

  h3 {
    margin: $size-grid-padding * 2.25 0 $size-grid-padding * 1.5;
  }

  h4 {
    margin: $size-grid-padding * 1.875 0 $size-grid-padding * 1.25;
  }

  h5,
  h6 {
    margin: $size-grid-padding * 1.5 0 $size-grid-padding;
  }
}

p {
  margin: 0 0 $size-grid-padding;
}

figure {
  margin: $size-grid-padding 0;
}

figcaption {
  font-style: italic;
  text-align: center;
}

img {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

label {
  @extend %typography-label;
}

ul,
ol {
  padding-left: $size-grid-padding;
}

@media only screen and (min-width: $size-breakpoint-sm + 1px) {
  .desktop {
    display: inline;
  }
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: $size-breakpoint-sm) {
  .desktop {
    display: none;
  }
  .mobile {
    display: inline;
  }
}

/* :not(.btn-check) keeps this rule from breaking Bootstrap toggle buttons */
input[type='checkbox']:not(.btn-check),
input[type='radio']:not(.btn-check) {
  position: relative;
  bottom: 1px;
  vertical-align: middle;
}

input[type='radio']:not(.btn-check) {
  bottom: 2px;
}

// ===
// Transitions
// ===

@media only screen and (min-width: $size-breakpoint-sm + 1px) {
  .fade-enter-active,
  .fade-in-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter,
  .fade-in-enter,
  .fade-leave-to,
  .fade-in-leave-active {
    opacity: 0;
  }
}

// ===
// Print
// ===

@media only print {
  // so all the content prints, not just one page
  * {
    overflow: visible !important;
  }

  // hide UI elements and map
  [class^='main_header'],
  [class^='main_footer'],
  [class^='property-list_sidebar'],
  [class^='mapbox'],
  [class^='property-show_controlsRow'],
  .hide-on-print {
    display: none !important;
  }

  // remove whitespace
  [class^='property-show_container'] {
    padding: 1.2rem !important;
  }

  [class^='_base-card_container'] {
    margin-right: -1.2rem !important;
    margin-left: -1.2rem !important;
  }

  [class^='_base-table_table'] {
    max-width: 250px !important;
  }

  [class^='property-photo_figure'] {
    max-width: 250px !important;
    margin-right: 0 !important;
  }
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: $color-link-text;
}

.tooltip {
  @extend %typography-label;

  z-index: 10000;
  display: block !important;

  .tooltip-inner {
    padding: 5px 10px 4px;
    color: white;
    background: black;
    border-radius: 3px;
  }

  .tooltip-arrow {
    position: absolute;
    z-index: 1;
    width: 0;
    height: 0;
    margin: 5px;
    border-color: black;
    border-style: solid;
  }

  &[x-placement^='top'] {
    margin-bottom: 5px;

    .tooltip-arrow {
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
      border-width: 5px 5px 0 5px;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      border-left-color: transparent !important;
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 5px;

    .tooltip-arrow {
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
      border-width: 0 5px 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-left-color: transparent !important;
    }
  }

  &[x-placement^='right'] {
    margin-left: 5px;

    .tooltip-arrow {
      top: calc(50% - 5px);
      left: -5px;
      margin-right: 0;
      margin-left: 0;
      border-width: 5px 5px 5px 0;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      border-left-color: transparent !important;
    }
  }

  &[x-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      top: calc(50% - 5px);
      right: -5px;
      margin-right: 0;
      margin-left: 0;
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
    }
  }

  &[x-placement='top-start'] {
    .tooltip-arrow {
      bottom: -5px;
      left: 2px !important;
      margin-top: 0;
      margin-bottom: 0;
      border-width: 5px 5px 0 5px;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      border-left-color: transparent !important;
    }
  }

  &.popover {
    $color: #f9f9f9;

    .popover-inner {
      padding: 24px;
      color: black;
      background: $color;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 0.15s,
      visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}
</style>
